import React, { JSX, useEffect, useState } from "react";
import { isNull } from "lodash";
import Paper from "@mui/material/Paper";
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Tooltip from '@mui/material/Tooltip';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import PublishIcon from '@mui/icons-material/Publish';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import useLzFApiCall from "@lz-services/Api";
import { useLzOnMount } from "@lz-services/Hooks";
import { lzFGetFormattedDateTime } from "@lz-services/Moment";
import LzXSpinner from "@lz-components/commons/Spinner";
import { lzFBytesToSize, lzFShowSuccessMessage } from "@lz-services/Utils";
import LzMBase, { LzTBase } from "@lz-models/commons/Base";
import LzMBackupRestore from "@lz-models/commons/BackupRestore";

export interface LzPPageBackupRestore {
    modelRef?: LzTBase,
    children?: React.ReactNode,
}

const LzXPageBackupRestore = (props: LzPPageBackupRestore) => {
    const [backups, setBackups] = useState<LzMBase[] | null>(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [recordsPerPage, setRecordsPerPage] = useState(10);

    const lzApiCall = useLzFApiCall();

    const load = async () => {
        const data = await lzApiCall('backups', {
            _page_index: currentPage + 1,
            _page_size: recordsPerPage,
        });
        if (data && data.data) {
            setBackups(LzMBase.parseArray(data.data, props.modelRef || LzMBackupRestore));
            setTotalRecords(+data.total);
            setCurrentPage(+data.current_page - 1);
            setRecordsPerPage(+data.per_page);
        }
    };

    // Call only for the first time when component is initially loaded
    useLzOnMount(() => {
        load();
    });

    useEffect(() => {
        load();
    }, [currentPage, recordsPerPage]);

    const handleCreateBackup = async () => {
        const data = await lzApiCall('backups/create');
        if (data) {
            lzFShowSuccessMessage('Backup created successfully!');
            load();
        }
    };

    const handleRestoreBackup = async (filename: string) => {
        if (!window.confirm('Are you sure you want to restore this backup?' + "\n" + 'It will OVERWRITE the current working database if you continue now!')) {
            return;
        }
        const data = await lzApiCall('backups/restore/' + filename);
        if (data) {
            lzFShowSuccessMessage('Backup restored successfully!');
            load();
        }
    };

    const handleDownloadBackup = async (filename: string) => {
        const data = await lzApiCall('backups/download/' + filename);
        if (data) {
            window.open(data, '_blank');
        }
    };

    const handleDeleteBackup = async (filename: string) => {
        if (!window.confirm('Are you sure you want to delete this backup?')) {
            return;
        }
        const data = await lzApiCall('backups/delete/' + filename);
        if (data) {
            lzFShowSuccessMessage('Backup deleted successfully!');
            load();
        }
    };

    const onPageChange = (pageNo: number) => {
        setCurrentPage(pageNo);
    };

    const onRecordsPerPageChange = (recordsPerPage: number) => {
        setCurrentPage(0);
        setRecordsPerPage(recordsPerPage);
    };

    return (
        <React.Fragment>
            {props.children ?
                props.children
                :
                <Paper elevation={3} style={{ width: '100%' }}>

                    <div style={{ padding: 24, textAlign: 'right' }}>
                        <Button onClick={load} color="primary" style={{ marginRight: 12 }}><RefreshIcon />Reload List</Button>
                        <Button onClick={handleCreateBackup} color="primary" variant="contained"><CloudDoneIcon />Create Backup</Button>
                    </div>

                    {isNull(backups) ?
                        <LzXSpinner />
                        :
                        <React.Fragment>
                            {backups.length < 1 ? (
                                <Alert color="warning">No existing backups found!</Alert>
                            ) : (
                                <React.Fragment>
                                    <TableContainer style={{ maxHeight: '70vh' }}>
                                        <Table stickyHeader className="lz-crud-listing-table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="left" className="lz-list-head">Backup Datetime</TableCell>
                                                    <TableCell align="left" className="lz-list-head">File Size</TableCell>
                                                    <TableCell align="center" style={{ width: 1 }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {backups.map((rec, dex) =>
                                                    <TableRow hover key={'trKey-' + dex}>
                                                        <TableCell align="left">{lzFGetFormattedDateTime(rec.datetime)}</TableCell>
                                                        <TableCell align="left">{lzFBytesToSize(rec.filesize)}</TableCell>
                                                        <TableCell padding="none" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            <Tooltip title="Restore Backup">
                                                                <IconButton className="lz-icon-button-warning" onClick={() => handleRestoreBackup(rec.filename)}>
                                                                    <PublishIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Download Backup">
                                                                <IconButton className="lz-icon-button-success" onClick={() => handleDownloadBackup(rec.filename)}>
                                                                    <CloudDownloadIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete Backup">
                                                                <IconButton className="lz-icon-button-error" onClick={() => handleDeleteBackup(rec.filename)}>
                                                                    <DeleteForeverIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        component="div"
                                        page={currentPage || 0}
                                        count={totalRecords || 0}
                                        onPageChange={(evt, page) => onPageChange(+page)}
                                        rowsPerPage={recordsPerPage || 10}
                                        onRowsPerPageChange={(evt) => onRecordsPerPageChange(+evt.target.value)}
                                    />
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    }

                </Paper>
            }
        </React.Fragment>
    );
};

export default LzXPageBackupRestore;
